import { parseDate } from '@internationalized/date';
import { AxiosError } from 'axios';
import useSWR from 'swr';

import {
  camelizeKeys,
  today,
  WithStatusT,
} from '@consumer-frontend/nc-utils';

import { apiEndpoints } from '../lib';

type ScheduledPaymentT = {
  amount: number;
  id: number;
  is_autopay: boolean;
  is_future: boolean;
  is_obligational: boolean;
  is_payment_pending: boolean;
  is_payment_plan: boolean;
  is_representment: boolean;
  target_date: string; // ISO 8601 date
  due_date?: string; // ISO 8601 date
};

type ScheduledPaymentsResponseT = {
  scheduled_payments: ScheduledPaymentT[];
};

export type ScheduledPaymentCamelizedT = {
  amount: number;
  id: number;
  isAutopay: boolean;
  isFuture: boolean;
  isObligational: boolean;
  isPaymentPending: boolean;
  isPaymentPlan: boolean;
  isRepresentment: boolean;
  targetDate: string;
  dueDate?: string;
};

export type UseScheduledPaymentsPropsT = {
  accountId: string;
  loanNumber?: string;
};

export const useScheduledPayments = ({ accountId, loanNumber }: UseScheduledPaymentsPropsT) => {
  const queryParams: Record<string, string> = { end_date: today().add({ days: 35 }).toString() };

  // eslint-disable-next-line @stylistic/object-curly-newline
  const { data, error, isLoading } = useSWR<WithStatusT<ScheduledPaymentsResponseT>, AxiosError>(
    () => (accountId && loanNumber ? apiEndpoints.dashboard.scheduledPayments(accountId, loanNumber, queryParams) : null),
  );

  const scheduledPayments = data?.scheduled_payments
    .map((payment) => ({
      ...camelizeKeys<ScheduledPaymentT, ScheduledPaymentCamelizedT>(payment),
      dueDate: payment.due_date ? parseDate(payment.due_date) : undefined,
      targetDate: parseDate(payment.target_date),
    }))
    .sort((a, b) => a.targetDate.compare(b.targetDate))
  ;

  const obligationalScheduledPayments = scheduledPayments?.filter((sp) => sp.isObligational) || [];
  const nextObligationalScheduledPayment = obligationalScheduledPayments.reduce(
    (sp, nextSp) => (sp.targetDate.compare(nextSp.targetDate) < 0 ? sp : nextSp),
    obligationalScheduledPayments[0],
  );
  const nonZeroScheduledPayments = scheduledPayments?.filter((sp) => sp.amount > 0) || [];

  return {
    error,
    isLoading,
    nextObligationalScheduledPayment,
    scheduledPayments: nonZeroScheduledPayments,
    status: data?.status || error?.status,
  };
};
